/**
 * CSV export helper.
 *
 * buildCsv() is a pure string builder so it can be unit-tested without a DOM.
 * exportToCsv() wraps it in a Blob download for browser use.
 *
 * Columns are described as [{ key, header }]: the header row is built from
 * each column.header, and each data row reads row[column.key].
 */

// Escape a single CSV field. Values containing a quote, comma, or newline are
// wrapped in double quotes with any internal quotes doubled (RFC 4180).
function escapeCsvValue(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/["\r\n,]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string from rows + column definitions.
 * @param {Array<object>} rows
 * @param {Array<{key: string, header: string}>} columns
 * @returns {string} CSV text with \r\n line endings (no trailing newline)
 */
export function buildCsv(rows, columns) {
  const headerLine = columns.map((c) => escapeCsvValue(c.header)).join(",");
  const dataLines = (rows || []).map((row) =>
    columns.map((c) => escapeCsvValue(row[c.key])).join(",")
  );
  return [headerLine, ...dataLines].join("\r\n");
}

/**
 * Build a CSV string and trigger a browser download.
 * @param {string} filename
 * @param {Array<object>} rows
 * @param {Array<{key: string, header: string}>} columns
 */
export function exportToCsv(filename, rows, columns) {
  const csv = buildCsv(rows, columns);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
